import React, { useState, useEffect, useRef } from 'react'
import {
  Dropdown,
  Button,
  Popover,
  Menu,
  Spin,
  Icon,
  Tooltip,
  Progress,
  Modal,
  Switch
} from 'antd'
import VideoPlayer from './VideoPlayer'
import CommentInput from '../../../CommentInput'
import TransitionSelector from '../Transition/TransitionSelector'
import TransitionDisplay from '../Transition/TransitionDisplay'
import VideoHistoryModal from '../VideoHistoryModal'
import VideoEditorModal from './VideoEditorModal'
import ShotVolumeSettings from './ShotVolumeSettings'
import {
  DownOutlined,
  SettingOutlined,
  EditOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  DeleteOutlined,
  UploadOutlined
} from '@ant-design/icons'
import EntitySelectorList from '../../../EntitySelectorList'
import { ImagePreview } from '../../../../../../components'
import ImageHistoryModal from '../../../ImageGeneration/components/ImageHistoryModal'
import DialogueEditor from '../../../ImageGeneration/components/DialogueEditor'
import SceneTransition from './SceneTransition'
import EstimatedDurationBadge from '../EstimatedDurationBadge'
import VideoUploadModal from './VideoUploadModal'
import ShotFrameModal, {
  ShotFrameImageOverlay
} from '../../../ShotFrames/ShotFrameModal'
import {
  FRAME_ROLES,
  createShotFrameUpdate,
  createShotFramesUpdate,
  getShotFrameUrl,
  removeShotFrame
} from '../../../ShotFrames/frameUtils'

const GeneratingText = ({ text = 'Generating' }) => {
  const [dots, setDots] = useState('')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : prev + '.'))
    }, 500)
    return () => clearInterval(interval)
  }, [])

  return (
    <div style={{ marginTop: 8, fontWeight: 500 }}>
      {text}
      {dots}
    </div>
  )
}

const VIDEO_TASK_PENDING_STATUSES = ['pending', 'processing']

const getVideoTaskStatus = shot => {
  if (
    shot?.videoGenerationStatus &&
    typeof shot.videoGenerationStatus === 'object'
  ) {
    return shot.videoGenerationStatus.status
      ? shot.videoGenerationStatus.status.toLowerCase()
      : null
  }

  return shot?.statusId ? 'pending' : null
}

const Shot = ({
  shot,
  sceneId,
  index,
  isLastShot,
  formData,
  transitions,
  handleRegenerateVideo,
  handleStopVideoGeneration,
  setScenes,
  handleUploadVideo,
  handleEditSettings,
  handleTransitionSelect,
  handleApplyTransitionToAll,
  handleRemoveAllTransitions,
  isGeneratingAll = false,
  videoGenerating = [],
  videoProvider,
  imageProvider,
  DragHandle,
  updateShotDataRef,
  // Image generation props
  handleRegenerateImage,
  handleRegenerateWithReferenceNanoBanana,
  isImageGenerationLoading,
  handleDeleteShot,
  videoId,
  isFetchingCharacters,
  frameCopyTargets,
  shotViewMode = 'expanded'
}) => {
  const [isImageMode, setIsImageMode] = useState(false)
  const [hasBeenVisible, setHasBeenVisible] = useState(false)
  const [historyModalVisible, setHistoryModalVisible] = useState(false)
  const [imageHistoryVisible, setImageHistoryVisible] = useState(false)
  const [videoEditorVisible, setVideoEditorVisible] = useState(false)
  const [volumeSettingsVisible, setVolumeSettingsVisible] = useState(false)
  const [compactVideoPreviewVisible, setCompactVideoPreviewVisible] = useState(
    false
  )
  const [frameModalVisible, setFrameModalVisible] = useState(false)
  const [frameModalInitialRole, setFrameModalInitialRole] = useState(
    FRAME_ROLES.end
  )
  const [framePreviewRole, setFramePreviewRole] = useState(FRAME_ROLES.start)

  const videoWrapperRef = useRef(null)
  const regenerationTriggeredRef = useRef(new Set())

  const [uploadModalVisible, setUploadModalVisible] = useState(false)

  const isVideoEditing = !!shot?.videoEditTaskId
  const isStaticShot = !!shot?.is_static
  const effectiveIsImageMode = isStaticShot || isImageMode
  const isCompactView = shotViewMode === 'compact'
  const startFrameUrl = getShotFrameUrl(shot, FRAME_ROLES.start)
  const endFrameUrl = getShotFrameUrl(shot, FRAME_ROLES.end)
  const frameSourceSignature = `${shot?.imageSrc || ''}::${endFrameUrl || ''}`
  const hasEndFrame = !!endFrameUrl
  const displayedFrameUrl =
    framePreviewRole === FRAME_ROLES.end && hasEndFrame
      ? endFrameUrl
      : startFrameUrl || shot.imageSrc

  const isProduction = process.env.NODE_ENV === 'production'

  const isVideoGeneratingFromQueue = videoGenerating.some(
    status => status.sceneId === sceneId && status.shotId === shot.id
  )
  const isVideoGeneratingFromStatus =
    !isStaticShot &&
    VIDEO_TASK_PENDING_STATUSES.includes(getVideoTaskStatus(shot))
  const isVideoGenerating =
    isVideoGeneratingFromQueue || isVideoGeneratingFromStatus

  const confirmStopVideoGeneration = e => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (!handleStopVideoGeneration) return

    Modal.confirm({
      title: 'Stop video generation?',
      content:
        'This will stop waiting for this generated clip. You can edit the prompt and regenerate again.',
      okText: 'Stop generation',
      cancelText: 'Keep generating',
      okType: 'danger',
      centered: true,
      onOk: () => handleStopVideoGeneration(sceneId, shot.id)
    })
  }

  useEffect(() => {
    if (isStaticShot) {
      setIsImageMode(true)
    }
  }, [isStaticShot])

  const handleCancelVideoEditing = () => {
    updateShotDataRef.current(sceneId, shot.id, {
      videoEditTaskId: null
    })
  }

  const handleStaticChange = checked => {
    setScenes(prevScenes =>
      prevScenes.map(scene =>
        scene.id === sceneId
          ? {
              ...scene,
              shots: scene.shots.map(item =>
                item.id === shot.id
                  ? {
                      ...item,
                      is_static: checked,
                      ...(checked ? { errorGeneratingVideo: false } : {})
                    }
                  : item
              )
            }
          : scene
      )
    )
  }

  const updateCurrentShot = updates => {
    setScenes(prevScenes =>
      prevScenes.map(scene =>
        scene.id === sceneId
          ? {
              ...scene,
              shots: scene.shots.map(item =>
                item.id === shot.id ? { ...item, ...updates } : item
              )
            }
          : scene
      )
    )
  }

  const openFrameModal = (role = FRAME_ROLES.end) => {
    setFrameModalInitialRole(role)
    setFrameModalVisible(true)
  }

  const handleSaveFrames = nextFrames => {
    updateCurrentShot(createShotFramesUpdate(shot, nextFrames))
  }

  const handleRemoveEndFrame = () => {
    updateCurrentShot(removeShotFrame(shot, FRAME_ROLES.end))
    setFramePreviewRole(FRAME_ROLES.start)
  }

  const handleOpenFrameImageEditor = role => {
    handleRegenerateWithReferenceNanoBanana(sceneId, shot.id, role)
  }

  useEffect(() => {
    if (!hasEndFrame && framePreviewRole === FRAME_ROLES.end) {
      setFramePreviewRole(FRAME_ROLES.start)
    }
  }, [hasEndFrame, framePreviewRole])

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true)
        } else {
          setHasBeenVisible(false)
        }
      },
      {
        rootMargin: '200px',
        threshold: 0.01
      }
    )

    if (videoWrapperRef.current) {
      observer.observe(videoWrapperRef.current)
    }

    return () => {
      if (videoWrapperRef.current) {
        observer.unobserve(videoWrapperRef.current)
      }
    }
  }, [])

  // Lazy initialization of video source metadata for legacy shots and backfill history
  useEffect(() => {
    if (
      !shot?.imageSrc ||
      (shot?.video_source_image !== undefined &&
        shot?.video_source_frames !== undefined)
    ) {
      return
    }

    const updates = {
      video_source_image: shot.imageSrc,
      video_source_frames: frameSourceSignature
    }

    // Try to backfill source_image in history if it's missing for the current video
    if (shot.vidSrc && shot.videos_history?.length > 0) {
      const historyUpdate = shot.videos_history.map(v => {
        if (v.url === shot.vidSrc && !v.source_image) {
          return {
            ...v,
            source_image: shot.imageSrc,
            source_frames: frameSourceSignature
          }
        }
        return v
      })

      // Only update if changes were made
      if (
        JSON.stringify(historyUpdate) !== JSON.stringify(shot.videos_history)
      ) {
        updates.videos_history = historyUpdate
      }
    }

    updateShotDataRef.current(sceneId, shot.id, updates)
  }, [
    shot?.imageSrc,
    shot?.video_source_image,
    shot?.video_source_frames,
    shot?.vidSrc,
    frameSourceSignature,
    sceneId,
    shot?.id
  ])

  // Auto-regenerate video when switching to video mode if image has changed
  useEffect(() => {
    // Only proceed if we're in video mode and not currently generating
    if (effectiveIsImageMode || isVideoGenerating || isStaticShot) {
      return
    }

    // Wait for proper initialization
    if (!shot?.imageSrc || !shot?.vidSrc) {
      return
    }

    // Check if frames changed from the visual source used to generate current video
    const previousSourceSignature =
      shot.video_source_frames || `${shot.video_source_image || ''}::`

    if (frameSourceSignature === previousSourceSignature) {
      return // Frames haven't changed, no action needed
    }

    // Create unique key for this regeneration to prevent duplicates
    const regenKey = `${sceneId}-${shot.id}-${frameSourceSignature}`

    if (regenerationTriggeredRef.current.has(regenKey)) {
      return // Already triggered for this image
    }

    // Image has changed, check if we have this video in history
    const existingVideo = shot.videos_history?.find(
      v =>
        v.source_frames === frameSourceSignature ||
        (!hasEndFrame && v.source_image === shot.imageSrc)
    )

    if (existingVideo) {
      // Use existing video from history
      updateShotDataRef.current(sceneId, shot.id, {
        vidSrc: existingVideo.url,
        video_source_image: shot.imageSrc,
        video_source_frames: frameSourceSignature
      })
    } else {
      // Regenerate video with new image
      regenerationTriggeredRef.current.add(regenKey)

      // Update video_source_image first
      updateShotDataRef.current(sceneId, shot.id, {
        video_source_image: shot.imageSrc,
        video_source_frames: frameSourceSignature
      })

      // Then trigger regeneration
      handleRegenerateVideo(sceneId, shot.id)
    }
  }, [
    effectiveIsImageMode,
    isStaticShot,
    shot?.imageSrc,
    frameSourceSignature,
    shot?.video_source_image,
    shot?.video_source_frames,
    isVideoGenerating,
    sceneId,
    shot?.id
  ])

  const isError = shot && shot.errorGeneratingVideo

  // Memoize video source to prevent VideoPlayer re-renders
  const videoSrc = shot.vidSrc
  const imageSrc = shot.imageSrc
  const canEditVideo = !!videoSrc && !isVideoGenerating

  const handlePrimaryVideoRegenerate = () => {
    handleRegenerateVideo(sceneId, shot.id)
    updateShotDataRef.current(sceneId, shot.id, {
      video_source_image: shot.imageSrc,
      video_source_frames: frameSourceSignature
    })
  }

  return (
    <React.Fragment>
      <div
        className={`shot-card shot-card--${shotViewMode} ${
          effectiveIsImageMode ? 'is-edit-mode' : ''
        } ${
          !isVideoGenerating && isError && !effectiveIsImageMode
            ? 'error-generating-video'
            : ''
        }`}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h3
            className="shot-title"
            style={{ margin: 0, display: 'flex', alignItems: 'center' }}
          >
            {DragHandle ? <DragHandle /> : null}
            <span>Shot {index + 1}</span>
            <EstimatedDurationBadge durationValue={shot.video_duration} />
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div
              className={`mode-toggle-container ${
                isVideoGenerating || isStaticShot ? 'disabled' : ''
              }`}
            >
              <div
                className={`mode-toggle-item image ${
                  effectiveIsImageMode ? 'active' : ''
                }`}
                onClick={() => setIsImageMode(true)}
              >
                <PictureOutlined />
                {effectiveIsImageMode && 'Image'}
              </div>
              <Tooltip
                title={
                  isStaticShot ? 'Static shots skip video generation' : 'Video'
                }
              >
                <div
                  className={`mode-toggle-item video ${
                    !effectiveIsImageMode ? 'active' : ''
                  } ${isStaticShot ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!isStaticShot) setIsImageMode(false)
                  }}
                >
                  <VideoCameraOutlined />
                  {!effectiveIsImageMode && 'Video'}
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        {isCompactView && (
          <div style={{ marginTop: 8 }}>
            <SceneTransition
              sceneId={sceneId}
              position={isLastShot ? 'end' : shot.id}
              transitionType={
                transitions[`${sceneId}-${isLastShot ? 'end' : shot.id}`]
              }
              handleTransitionSelect={handleTransitionSelect}
              handleApplyTransitionToAll={handleApplyTransitionToAll}
              handleRemoveAllTransitions={handleRemoveAllTransitions}
              compact
              compactLabel="Next"
            />
          </div>
        )}

        <div className="shot-card-stack">
          {/* VIDEO LAYER */}
          <div
            className={`shot-card-content video-layer ${
              !effectiveIsImageMode ? 'active' : 'inactive'
            }`}
          >
            <div
              className="video-wrapper"
              ref={videoWrapperRef}
              style={
                isError
                  ? {
                      border: '1px solid #ff4d4f',
                      position: 'relative'
                    }
                  : { position: 'relative' }
              }
            >
              {videoSrc && !isVideoGenerating && !isCompactView ? (
                <VideoPlayer
                  url={videoSrc}
                  isVisible={hasBeenVisible}
                  volume={(shot.volume !== undefined ? shot.volume : 25) / 100}
                />
              ) : (
                <img
                  src={imageSrc}
                  width="100%"
                  height="100%"
                  style={{
                    objectFit: 'contain',
                    filter: isError ? 'brightness(0.5)' : 'none'
                  }}
                  alt={`Shot ${shot.id}`}
                />
              )}
              {isCompactView &&
                videoSrc &&
                !isVideoGenerating &&
                !isError &&
                !isVideoEditing && (
                  <Button
                    shape="circle"
                    icon="caret-right"
                    onClick={() => setCompactVideoPreviewVisible(true)}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 52,
                      height: 52,
                      border: '1px solid rgba(255,255,255,0.55)',
                      background: 'rgba(0, 0, 0, 0.45)',
                      color: '#fff',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
                      zIndex: 11
                    }}
                  />
                )}
              {!isVideoGenerating && isError && (
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: '#fff',
                    border: '1px solid #ff4d4f',
                    color: '#ff4d4f',
                    borderRadius: 4,
                    padding: '2px 6px',
                    fontSize: 12,
                    fontWeight: 600,
                    zIndex: 10
                  }}
                >
                  Task failed. Please regenerate.
                </div>
              )}
              {isVideoGenerating && (
                <div className="video-generating-overlay">
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={shot.progress ? parseInt(shot.progress) : 0}
                      status="active"
                      width={60}
                      strokeColor="#1890ff"
                      trailColor="rgba(255,255,255,0.3)"
                      format={percent => (
                        <span style={{ color: 'white', fontSize: '12px' }}>
                          {percent}%
                        </span>
                      )}
                    />
                    <GeneratingText text="Generating" />
                    {handleStopVideoGeneration && (
                      <Button
                        size="small"
                        icon="stop"
                        onMouseDown={e => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onTouchStart={e => {
                          e.stopPropagation()
                        }}
                        onClick={e => {
                          confirmStopVideoGeneration(e)
                        }}
                        style={{
                          marginTop: 10,
                          height: 32,
                          padding: '0 14px',
                          borderRadius: 16,
                          background: 'rgba(255, 255, 255, 0.94)',
                          color: '#b42318',
                          border: '1px solid rgba(255, 204, 199, 0.95)',
                          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.22)',
                          fontWeight: 600,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          pointerEvents: 'auto',
                          position: 'relative',
                          zIndex: 11
                        }}
                      >
                        Stop
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {isVideoEditing && !isVideoGenerating && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 10,
                    background: '#722ed1',
                    color: '#fff',
                    padding: '4px 10px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    boxShadow: '0 2px 8px rgba(114,46,209,0.3)',
                    cursor: 'pointer'
                  }}
                  onClick={() => setVideoEditorVisible(true)}
                >
                  <Spin size="small" style={{ marginRight: 2 }} />
                  Edit in progress...
                </div>
              )}
              {/* Video History Button */}
              {shot.videos_history && shot.videos_history.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 10
                  }}
                >
                  <Tooltip
                    title={`View History (${shot.videos_history.length})`}
                  >
                    <Button
                      shape="round"
                      onClick={() => setHistoryModalVisible(true)}
                      disabled={isVideoGenerating}
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        padding: '4px 12px',
                        height: 'auto',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Icon type="history" style={{ fontSize: 16 }} />
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          lineHeight: 1
                        }}
                      >
                        {shot.videos_history.length}
                      </span>
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>

            <EntitySelectorList
              activePrompt={shot.video_prompt}
              onUpdatePrompt={newText =>
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'video_prompt',
                  newText
                )
              }
              formData={formData}
              disabled={isVideoGenerating}
              isFetchingCharacters={isFetchingCharacters}
            />

            <CommentInput
              characterList={formData.characterList}
              locationReference={formData.locationReference}
              selectedCharacter={formData.selectedCharacter}
              locationMap={formData.locationMap}
              outfitMap={formData.outfitMap}
              outfitsReference={formData.outfits_reference}
              outfits={formData.outfits}
              propMap={formData.propMap}
              propsReference={
                formData.props_reference || formData.propReference
              }
              text={shot.video_prompt}
              placeholder="Write your video prompt..."
              disabled={isVideoGenerating}
              onChange={value =>
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'video_prompt',
                  value
                )
              }
            />

            <DialogueEditor
              dialogue={shot.dialogue}
              formData={formData}
              disabled={isVideoGenerating}
              onChange={newDialogue =>
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'dialogue',
                  newDialogue
                )
              }
            />

            <div
              className="card-actions"
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'space-between'
              }}
            >
              <div className="split-regenerate-button">
                <Button
                  size="small"
                  icon="reload"
                  disabled={isVideoGenerating || isVideoEditing}
                  onClick={handlePrimaryVideoRegenerate}
                  className="split-regenerate-button__main"
                >
                  <span
                    style={{
                      marginLeft: 4,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    Regenerate Video
                  </span>
                </Button>
                <Dropdown
                  trigger={['click']}
                  overlay={
                    <Menu className="video-generation-menu">
                      <Menu.Item
                        disabled={!canEditVideo}
                        onClick={() => setVideoEditorVisible(true)}
                      >
                        <EditOutlined /> Edit Video
                        {!videoSrc && (
                          <span
                            style={{
                              marginLeft: 8,
                              color: '#8c8c8c',
                              fontSize: 11
                            }}
                          >
                            (Generate first)
                          </span>
                        )}
                        {isVideoEditing && (
                          <span
                            style={{
                              marginLeft: 8,
                              color: '#722ed1',
                              fontSize: 11
                            }}
                          >
                            (In Progress)
                          </span>
                        )}
                      </Menu.Item>
                      {/* upload video */}
                      <Menu.Item
                        onClick={() => setUploadModalVisible(true)}
                        disabled={isVideoGenerating}
                      >
                        <UploadOutlined /> Upload Video
                      </Menu.Item>
                    </Menu>
                  }
                  disabled={isVideoGenerating}
                  placement="bottomRight"
                >
                  <Button
                    size="small"
                    className="split-regenerate-button__trigger"
                    disabled={isVideoGenerating}
                    aria-label="More video actions"
                  >
                    <DownOutlined style={{ fontSize: 10 }} />
                  </Button>
                </Dropdown>
              </div>
              <Button
                size="small"
                disabled={isVideoGenerating}
                onClick={() => handleEditSettings(sceneId, shot.id)}
              >
                <SettingOutlined />
                <span
                  style={{
                    marginLeft: 4,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Shot Editor
                </span>
              </Button>
              {videoSrc && (
                <Button
                  size="small"
                  disabled={isVideoGenerating || !videoSrc}
                  onClick={() => setVolumeSettingsVisible(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    minWidth: '60px',
                    justifyContent: 'center'
                  }}
                >
                  {!shot.volume || shot.volume > 0 ? (
                    <Icon type="sound" />
                  ) : (
                    <Icon type="stop" />
                  )}
                  <span style={{ fontSize: '12px' }}>
                    {shot.volume !== undefined ? `${shot.volume}%` : '25%'}
                  </span>
                </Button>
              )}
            </div>
          </div>

          {/* IMAGE LAYER */}
          <div
            className={`shot-card-content image-layer ${
              effectiveIsImageMode ? 'active' : 'inactive'
            }`}
          >
            <div
              className="image-wrapper"
              style={
                shot.error
                  ? { border: '1px solid #ff4d4f', position: 'relative' }
                  : { position: 'relative' }
              }
            >
              {shot.isGenerating && shot.progress !== 100 && (
                <div
                  className="progress-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0,0,0,0.6)',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    borderRadius: '4px'
                  }}
                >
                  <Spin style={{ color: '#fff' }} size="large" />
                  <span className="progress-text" style={{ marginTop: '10px' }}>
                    {shot.progress ? 'Generated Successfully' : 'Generating...'}
                  </span>
                </div>
              )}
              {!shot.isGenerating && shot.error && (
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: '#fff',
                    border: '1px solid #ff4d4f',
                    color: '#ff4d4f',
                    borderRadius: 4,
                    padding: '2px 6px',
                    fontSize: 12,
                    fontWeight: 600,
                    zIndex: 20
                  }}
                >
                  Task failed. Please regenerate.
                </div>
              )}
              {displayedFrameUrl && (
                <ImagePreview
                  src={displayedFrameUrl || '/api/placeholder/400/300'}
                  alt={`${
                    framePreviewRole === FRAME_ROLES.end ? 'End' : 'Start'
                  } frame for shot ${shot.id}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
              {isStaticShot && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 10,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'linear-gradient(135deg, #fffaf0, #fff4e6)',
                    color: '#d46b08',
                    border: '1px solid #ffd591',
                    borderRadius: 999,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    boxShadow: '0 6px 14px rgba(250, 140, 22, 0.16)',
                    letterSpacing: 0.1
                  }}
                >
                  <Icon type="warning" style={{ fontSize: 12 }} />
                  <span>Static Image - Video Skipped</span>
                </div>
              )}

              {/* Image History Button */}
              {shot.images_history && shot.images_history.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 10
                  }}
                >
                  <Tooltip
                    title={`View History (${shot.images_history.length})`}
                  >
                    <Button
                      shape="round"
                      onClick={() => setImageHistoryVisible(true)}
                      disabled={shot.isGenerating}
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        color: '#fff',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        padding: '4px 12px',
                        height: 'auto',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Icon type="history" style={{ fontSize: 16 }} />
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          lineHeight: 1
                        }}
                      >
                        {shot.images_history.length}
                      </span>
                    </Button>
                  </Tooltip>
                </div>
              )}
              {!shot.isGenerating && (
                <ShotFrameImageOverlay
                  activeRole={framePreviewRole}
                  hasEndFrame={hasEndFrame}
                  disabled={isVideoGenerating}
                  onRoleChange={setFramePreviewRole}
                />
              )}
            </div>

            <EntitySelectorList
              activePrompt={shot.initial_image_prompt}
              onUpdatePrompt={newText =>
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'initial_image_prompt',
                  newText
                )
              }
              formData={formData}
              disabled={shot.isGenerating}
              videoId={videoId}
            />

            <CommentInput
              disabled={shot.isGenerating}
              text={shot.initial_image_prompt}
              placeholder="Write your image prompt..."
              characterList={formData.characterList}
              locationReference={formData.locationReference}
              selectedCharacter={formData.selectedCharacter}
              locationMap={formData.locationMap}
              outfitMap={formData.outfitMap}
              outfitsReference={formData.outfits_reference}
              outfits={formData.outfits}
              propMap={formData.propMap}
              propsReference={
                formData.props_reference || formData.propReference
              }
              onChange={value => {
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'initial_image_prompt',
                  value
                )
              }}
            />

            <DialogueEditor
              dialogue={shot.dialogue}
              formData={formData}
              disabled={shot.isGenerating}
              onChange={newDialogue =>
                updateShotDataRef.current(
                  sceneId,
                  shot.id,
                  'dialogue',
                  newDialogue
                )
              }
            />

            <div
              className="card-actions"
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                <div className="split-regenerate-button">
                  <Button
                    size="small"
                    icon="reload"
                    disabled={shot.isGenerating}
                    onClick={() => handleRegenerateImage(sceneId, shot.id)}
                    className="split-regenerate-button__main"
                  >
                    <span
                      style={{
                        marginLeft: 4,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      Regenerate
                    </span>
                  </Button>
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item
                          key="manage-shot-frames"
                          onClick={() =>
                            openFrameModal(
                              hasEndFrame ? FRAME_ROLES.start : FRAME_ROLES.end
                            )
                          }
                          disabled={shot.isGenerating}
                        >
                          <Icon type="picture" />{' '}
                          {hasEndFrame
                            ? 'Manage Start / End Frames'
                            : 'Add End Frame'}
                        </Menu.Item>
                        {hasEndFrame && (
                          <Menu.Item
                            key="remove-end-frame"
                            onClick={handleRemoveEndFrame}
                            disabled={shot.isGenerating}
                          >
                            <Icon type="delete" /> Reset End Frame
                          </Menu.Item>
                        )}
                        <Menu.Item
                          key="regenerate-with-reference-nano-banana"
                          onClick={() =>
                            handleRegenerateWithReferenceNanoBanana(
                              sceneId,
                              shot.id
                            )
                          }
                          disabled={shot.isGenerating || !shot.imageSrc}
                        >
                          <Icon type="file-image" /> Edit Image (Multiple
                          References)
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={['click']}
                    placement="bottomLeft"
                    disabled={shot.isGenerating}
                  >
                    <Button
                      size="small"
                      className="split-regenerate-button__trigger"
                      disabled={shot.isGenerating}
                      aria-label="More image actions"
                    >
                      <DownOutlined style={{ fontSize: 10 }} />
                    </Button>
                  </Dropdown>
                </div>
              </div>

              {/*
              <Tooltip title="Use this image directly in final assembly and skip clip generation">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    color: isStaticShot ? '#5057d5' : '#595959',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>Static</span>
                  <Switch
                    size="small"
                    className="static-toggle-switch"
                    checked={isStaticShot}
                    disabled={shot.isGenerating || isVideoGenerating}
                    onChange={handleStaticChange}
                  />
                </div>
              </Tooltip>
              */}

              <Button
                size="small"
                icon="setting"
                onClick={() => handleEditSettings(sceneId, shot.id)}
                disabled={shot.isGenerating}
              >
                <span
                  style={{
                    marginLeft: 4,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Shot Editor
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Scene Transition (between-shot rail only in expanded mode) */}
      {!isCompactView && (
        <SceneTransition
          sceneId={sceneId}
          position={isLastShot ? 'end' : shot.id}
          transitionType={
            transitions[`${sceneId}-${isLastShot ? 'end' : shot.id}`]
          }
          handleTransitionSelect={handleTransitionSelect}
          handleApplyTransitionToAll={handleApplyTransitionToAll}
          handleRemoveAllTransitions={handleRemoveAllTransitions}
        />
      )}

      {/* Video History Modal */}
      <VideoHistoryModal
        visible={historyModalVisible}
        history={shot.videos_history || []}
        onCancel={() => setHistoryModalVisible(false)}
        onSelect={url => {
          updateShotDataRef.current(sceneId, shot.id, {
            vidSrc: url,
            video_source_image: shot.imageSrc,
            video_source_frames: frameSourceSignature
          })
          setHistoryModalVisible(false)
        }}
        onDelete={index => {
          const updatedHistory = [...(shot.videos_history || [])]
          updatedHistory.splice(index, 1)
          updateShotDataRef.current(sceneId, shot.id, {
            videos_history: updatedHistory
          })
        }}
      />

      <ImageHistoryModal
        visible={imageHistoryVisible}
        history={shot.images_history || []}
        onCancel={() => setImageHistoryVisible(false)}
        onSelect={url => {
          updateCurrentShot(
            createShotFrameUpdate(shot, FRAME_ROLES.start, url, {
              source: 'history'
            })
          )
          setImageHistoryVisible(false)
        }}
        onDelete={index => {
          const updatedHistory = [...(shot.images_history || [])]
          updatedHistory.splice(index, 1)
          updateShotDataRef.current(sceneId, shot.id, {
            images_history: updatedHistory
          })
        }}
      />

      {/* Shot Volume Settings Modal */}
      <ShotVolumeSettings
        visible={volumeSettingsVisible}
        onCancel={() => setVolumeSettingsVisible(false)}
        onSave={volume => {
          setScenes(prevScenes =>
            prevScenes.map(s =>
              s.id === sceneId
                ? {
                    ...s,
                    shots: s.shots.map(sh =>
                      sh.id === shot.id ? { ...sh, volume } : sh
                    )
                  }
                : s
            )
          )
          setVolumeSettingsVisible(false)
        }}
        onMuteAll={() => {
          setScenes(prevScenes =>
            prevScenes.map(s => ({
              ...s,
              shots: s.shots.map(sh => ({ ...sh, volume: 0 }))
            }))
          )
          setVolumeSettingsVisible(false)
        }}
        videoUrl={shot.vidSrc}
        initialVolume={shot.volume !== undefined ? shot.volume : 25}
      />

      <Modal
        title={`Shot ${index + 1} Preview`}
        visible={compactVideoPreviewVisible}
        footer={null}
        width={920}
        centered
        maskClosable
        onCancel={() => setCompactVideoPreviewVisible(false)}
        destroyOnClose
      >
        <div
          style={{
            background: '#000',
            borderRadius: 8,
            overflow: 'hidden',
            aspectRatio: '16 / 9',
            maxHeight: '70vh'
          }}
        >
          <VideoPlayer
            url={videoSrc}
            isVisible={compactVideoPreviewVisible}
            volume={(shot.volume !== undefined ? shot.volume : 25) / 100}
            height="100%"
          />
        </div>
      </Modal>

      {/* Video Editor Modal */}
      <VideoEditorModal
        visible={videoEditorVisible}
        onCancel={() => setVideoEditorVisible(false)}
        onCancelVideoEditing={handleCancelVideoEditing}
        onConfirm={url => {
          updateShotDataRef.current(sceneId, shot.id, {
            vidSrc: url,
            video_source_image: shot.imageSrc,
            video_source_frames: frameSourceSignature,
            videos_history: [
              ...(shot.videos_history || []),
              {
                url: url,
                timestamp: Date.now(),
                duration_at_generation: shot.video_duration,
                source_image: shot.imageSrc,
                source_frames: frameSourceSignature
              }
            ],
            videoChatHistory: [],
            videoEditTaskId: null,
            videoEditProgress: 0
          })
          setVideoEditorVisible(false)
        }}
        initialPrompt={shot.video_prompt}
        currentVideo={shot.vidSrc}
        persistedConversation={shot.videoChatHistory || []}
        activeTaskId={shot.videoEditTaskId || null}
        onUpdateState={(history, taskId, progress) => {
          updateShotDataRef.current(sceneId, shot.id, {
            videoChatHistory: history,
            videoEditTaskId: taskId,
            videoEditProgress: progress
          })
        }}
        videoId={videoId}
      />

      <VideoUploadModal
        visible={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        onUploadComplete={url => {
          handleUploadVideo(sceneId, shot.id, url)
          setUploadModalVisible(false)
        }}
        videoId={videoId}
      />

      <ShotFrameModal
        visible={frameModalVisible}
        shot={shot}
        shotLabel={`Shot ${index + 1}`}
        aspectRatio={formData.aspectRatio || '16:9'}
        copyTargets={frameCopyTargets || []}
        initialRole={frameModalInitialRole}
        videoId={videoId}
        formData={formData}
        imageProvider={imageProvider}
        disabled={shot.isGenerating || isVideoGenerating}
        onCancel={() => setFrameModalVisible(false)}
        onSave={handleSaveFrames}
        onOpenImageEditor={handleOpenFrameImageEditor}
      />
    </React.Fragment>
  )
}

export default Shot
